import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { FuneralDrawer } from './FuneralDrawer';

type FuneralDrawerContextValue = {
  openFuneral: (id: number | string) => void;
};

const FuneralDrawerContext = createContext<FuneralDrawerContextValue | null>(null);

/**
 * Provides an `openFuneral(id)` action that opens the funeral in a right-side
 * drawer (mirroring the Inbox lead/funeral drawer pattern) instead of
 * navigating to the show page. Mounts a single shared `FuneralDrawer` so the
 * rows — which live across multiple status groups and the mobile list — can all
 * drive it.
 */
export function FuneralDrawerProvider({ children }: { children: React.ReactNode }) {
  const [funeralId, setFuneralId] = useState<number | string | null>(null);
  const [open, setOpen] = useState(false);

  const openFuneral = useCallback((id: number | string) => {
    setFuneralId(id);
    setOpen(true);
  }, []);

  const value = useMemo(() => ({ openFuneral }), [openFuneral]);

  return (
    <FuneralDrawerContext.Provider value={value}>
      {children}
      <FuneralDrawer funeralId={funeralId} open={open} onClose={() => setOpen(false)} />
    </FuneralDrawerContext.Provider>
  );
}

/** Opens a funeral in the shared drawer. Falls back to `undefined` outside a provider. */
export function useFuneralDrawer(): FuneralDrawerContextValue | null {
  return useContext(FuneralDrawerContext);
}
